/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from "@emotion/react";
import { useI18n } from "hooks/i18n/i18n";
import { Information } from "mdi-material-ui";
import React from "react";
import { useSelector } from "store";
import { convertDatetimeToHHII, getDateWithDayOfWeekString } from "utility";

import type { Store } from "store";

/**
 * もぎり無効時にQRコードの上に重ねる、半透明白のオーバーレイ。
 * QRはうっすら透けつつ、info + 「このチケットはまだ使えません」+ イベント開催日時カードを表示する。
 */
export const AccountTicketDigitalQrCodeDisabledOverlay: React.FC = () => {
  const { t } = useI18n();
  const displayEvent = useSelector((state: Store) => state.event.displayEvent);

  return (
    <div css={overlayStyle}>
      <div css={messageAreaStyle}>
        <Information css={infoIconStyle} />
        <p css={messageTextStyle}>{t("ticketDetail.ticketNotYetUsable")}</p>
      </div>

      {displayEvent && <EventScheduleCard displayEvent={displayEvent} />}
    </div>
  );
};

/**
 * イベント開催日時 + OPEN/START 時刻を表示するティール色のカード。
 */
const EventScheduleCard: React.FC<{
  displayEvent: NonNullable<Store["event"]["displayEvent"]>;
}> = ({ displayEvent }) => {
  const { t } = useI18n();
  const { parts, isLongEvent } = displayEvent;
  const [firstPart] = parts;
  const lastPart = parts[parts.length - 1];

  return (
    <div css={cardStyle}>
      <div css={sectionStyle}>
        <p css={labelStyle}>{t("ticketDetail.eventHeldDateTime")}</p>
        {firstPart && lastPart && (
          <p css={valueStyle}>
            {getDateWithDayOfWeekString(firstPart.openTime.toDate())}
            {isLongEvent && (
              <React.Fragment>
                <br />~<br />
                {getDateWithDayOfWeekString(lastPart.openTime.toDate())}
              </React.Fragment>
            )}
          </p>
        )}
      </div>

      <div css={dividerStyle} />

      <div css={sectionStyle}>
        <p css={labelStyle}>{t("ticketDetail.heading")}</p>
        <p css={valueStyle}>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {/* 複数公演(DAY1/DAY2等)のときだけ公演名を前置する */}
              {parts.length >= 2 && `${part.name} `}
              {convertDatetimeToHHII(part.openTime)} /{" "}
              {convertDatetimeToHHII(part.startTime)}
            </React.Fragment>
          ))}
        </p>
      </div>
    </div>
  );
};

const overlayStyle = css({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "16px",
  background: "rgba(255, 255, 255, 0.97)",
});

const messageAreaStyle = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "4px",
});

const infoIconStyle = css({
  width: "44px",
  height: "44px",
  color: "#319795",
});

const messageTextStyle = css({
  fontSize: "14px",
  fontWeight: "bold",
  lineHeight: "20px",
  color: "#000",
  textAlign: "center",
});

const cardStyle = css({
  width: "226px",
  maxWidth: "100%",
  background: "#e6fffa",
  borderRadius: "12px",
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
});

const sectionStyle = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "4px",
});

const labelStyle = css({
  fontSize: "10px",
  fontWeight: 500,
  lineHeight: "14px",
  color: "#285e61",
});

const valueStyle = css({
  fontSize: "14px",
  fontWeight: "bold",
  lineHeight: "20px",
  color: "#000",
  textAlign: "center",
});

const dividerStyle = css({
  width: "210px",
  maxWidth: "100%",
  height: "1px",
  background: "#b2f5ea",
});
